import req from '@sportninja/common/api/request'
import { normalizeOptionLabel } from '@sportninja/common/utils/optionLabel'
import { useCallback, useState } from 'react'

/**
 * Represents a custom form object.
 */
export interface CustomForm {
  id: string
  name: string
  description: string | null
  groups: unknown[]
  created_at: string
  updated_at: string
}

/**
 * Represents a form group object.
 */
export interface Group {
  id: string
  name: string
  description: string | null
  is_enabled: number // API returns 0 or 1
  is_reserved: number // API returns 0 or 1
  is_content?: number // API returns 0 or 1 for content sections
  content?: string // Rich text content for content sections
  index: number
  form_id?: string // Optional as it may not always be included
  items: FormItem[] // API includes items array
  created_at: string
  updated_at?: string // Optional as it may not always be included
}

/**
 * Represents a form item type object.
 */
export interface FormItemType {
  id: number
  name: string
  parameters?: Record<string, string> // e.g., { "max_char": "int" }
}

/**
 * Represents a form item object.
 */
export interface FormItem {
  id: string
  name: string
  display_label?: string | null
  description: string | null
  form_item_type: FormItemType // Changed from form_item_type_id to form_item_type object
  is_enabled: number // API returns 0 or 1
  is_required: number // API returns 0 or 1
  is_reserved: number // API returns 0 or 1
  index: number
  group_id: string
  parameters?: {
    values?: Array<{
      name: string
      value: string
      index: number
    }>
    // Number type parameters
    min_number?: string | number
    max_number?: string | number
    // Legacy support for old parameter names
    min?: string | number
    max?: string | number
    // Text type parameters
    max_char?: string | number
    // Hockey USA specific parameters
    validate_schedule_dates?: boolean
    usa_hockey_validation_type?: 'none' | 'full' | 'partial'
  } // For dropdown options, number range constraints, and text length limits
  created_at: string
  updated_at?: string // Optional as it may not always be included
}

/**
 * Form item type constants based on the available options.
 */
export const FORM_ITEM_TYPES = {
  TEXT_FIELD: 1,
  NUMBER_FIELD: 2,
  DROPDOWN: 3,
  CHECKBOX: 4,
  TEXT_AREA: 5,
  DATE: 6,
  BIRTH_DATE: 7,
  PHONE: 8,
  EMAIL: 9,
  URL: 10,
  COUNTRY: 11,
  PLAYER_POSITION: 12,
  HOCKEY_USA: 13,
  PLAYER_NUMBER: 14,
  RELATIONSHIP: 15,
  TEAM_OFFICIAL_ROLE: 16,
  // 17 is Multi-Select Dropdown (SN-8081).
} as const

/**
 * Form item type options for UI selection.
 */
export const FORM_ITEM_TYPE_OPTIONS = [
  { id: FORM_ITEM_TYPES.TEXT_FIELD, name: 'Text Field' },
  { id: FORM_ITEM_TYPES.NUMBER_FIELD, name: 'Number Field' },
  { id: FORM_ITEM_TYPES.DROPDOWN, name: 'Dropdown' },
  { id: FORM_ITEM_TYPES.CHECKBOX, name: 'Checkbox' },
  { id: FORM_ITEM_TYPES.TEXT_AREA, name: 'Text Area' },
  { id: FORM_ITEM_TYPES.DATE, name: 'Date' },
  { id: FORM_ITEM_TYPES.BIRTH_DATE, name: 'Birth Date' },
  { id: FORM_ITEM_TYPES.PHONE, name: 'Phone' },
  { id: FORM_ITEM_TYPES.EMAIL, name: 'Email' },
  { id: FORM_ITEM_TYPES.URL, name: 'URL' },
  { id: FORM_ITEM_TYPES.COUNTRY, name: 'Country' },
  { id: FORM_ITEM_TYPES.PLAYER_POSITION, name: 'Player Position' },
  { id: FORM_ITEM_TYPES.HOCKEY_USA, name: 'USA Hockey' },
  { id: FORM_ITEM_TYPES.PLAYER_NUMBER, name: 'Player Number' },
] as const

/**
 * Represents a custom form object with typed groups.
 */
export interface CustomFormWithGroups extends Omit<CustomForm, 'groups'> {
  groups: Group[]
}

interface CreateFormData {
  name: string
  id: string
}

interface CreateGroupData {
  form_id: string
  name: string
  description?: string
  is_enabled: boolean
  is_reserved: boolean
  is_content?: boolean
  content?: string
  index: number
}

interface UpdateFormData {
  name?: string
  description?: string
}

interface UpdateGroupData {
  name?: string
  description?: string
  is_enabled?: boolean
  is_reserved?: boolean
  is_content?: boolean
  content?: string
  index?: number
}

interface ReorderGroupData {
  id: string
  index: number
}

interface CreateItemData {
  group_id: string
  name: string
  description?: string
  form_item_type_id: number // We still send the ID when creating
  is_enabled: boolean
  is_required: boolean
  is_reserved: boolean
  index: number
  parameters?: {
    values?: Array<{
      name: string
      value: string
      index: number
    }>
    // Number type parameters
    min_number?: string | number
    max_number?: string | number
    // Legacy support for old parameter names
    min?: string | number
    max?: string | number
    // Text type parameters
    max_char?: string | number
    // Hockey USA specific parameters
    validate_schedule_dates?: boolean
    usa_hockey_validation_type?: 'none' | 'full' | 'partial'
  } // For dropdown options, number range constraints, and text length limits
  form_id?: string // Optional as it may not always be included
}

interface UpdateItemData {
  name?: string
  description?: string
  form_item_type_id?: number // We still send the ID when updating
  is_enabled?: boolean
  is_required?: boolean
  is_reserved?: boolean
  index?: number
  parameters?: {
    values?: Array<{
      name: string
      value: string
      index: number
    }>
    // Number type parameters
    min_number?: string | number
    max_number?: string | number
    // Legacy support for old parameter names
    min?: string | number
    max?: string | number
    // Text type parameters
    max_char?: string | number
    // Hockey USA specific parameters
    validate_schedule_dates?: boolean
    usa_hockey_validation_type?: 'none' | 'full' | 'partial'
  } // For dropdown options, number range constraints, and text length limits
}

interface ReorderItemData {
  id: string
  index: number
}

/**
 * Converts legacy options array to parameters format for dropdown questions.
 * Ensures that the value field always matches the label field.
 *
 * This is the SINGLE chokepoint for every builder save — create and update, in
 * both the Player and Team builders — so it is where normalization has to
 * happen. Paste List dedupes at parse time; Rows mode has no parser, so without
 * this a registrant could be shown the same option twice
 * (`Adray Arena, Adray Arena, Yack Arena`), and a padded pair (`Adray Arena`
 * plus `Adray Arena␣␣`) arrives distinct and lands as two byte-identical rows.
 *
 * Normalization, in order:
 *   - canonicalize each label with `normalizeOptionLabel` — trim, and collapse
 *     repeated inner whitespace. Both are invisible to a registrant, so both
 *     have to be folded away before duplicates can be detected at all
 *   - drop labels that are empty once normalized: blank editor rows are not
 *     options, and `Options (N)` already counts only non-blank ones
 *   - drop duplicates, keeping the FIRST occurrence, then reindex 1..n
 *
 * Duplicate detection is CASE-SENSITIVE, matching `parseOptionText`'s
 * `seenExact`: two options differing only by case are visibly different, so
 * they are a legitimate (if confusing) list and the editor warns about them
 * rather than removing them.
 *
 * Returns undefined for an empty list, which OMITS `parameters` from the PUT
 * rather than clearing it. The builder's save validation makes that unreachable
 * (a dropdown always needs at least one non-blank option), and it must stay
 * that way — see the round-trip tests.
 */
export const convertOptionsToParameters = (options: any[]) => {
  if (!options || options.length === 0) {
    return undefined
  }

  const seen = new Set<string>()
  const values: Array<{ name: string; value: string; index: number }> = []

  options.forEach((option) => {
    const label = normalizeOptionLabel(option?.label ?? '')

    if (!label || seen.has(label)) {
      return
    }
    seen.add(label)

    values.push({
      name: label,
      value: label, // Always use label as value to ensure consistency
      index: values.length + 1, // API expects 1-based indexing
    })
  })

  if (values.length === 0) {
    return undefined
  }

  return { values }
}

/**
 * Converts a form item's stored `parameters.values` back into the builder's
 * option list — the inverse of convertOptionsToParameters, kept beside it so
 * the two directions cannot drift.
 *
 * The `id` is POSITIONAL, not the option's value. Ids used to be `value.value`,
 * which after a reload made an option's id its own label: two options sharing a
 * label then edited together, deleted together, and emitted duplicate React
 * keys. `id` is only a React key and an edit/delete handle —
 * convertOptionsToParameters reads `option.label` alone — so making it
 * positional is safe, and it repairs editing on forms that already contain
 * duplicate labels.
 *
 * Sorts a COPY: `parameters.values` belongs to the API response object, and
 * sorting it in place mutated shared state.
 *
 * @param parameters - The form item's stored parameters
 * @returns Builder options in persisted index order
 */
export const convertParametersToOptions = (
  parameters?: FormItem['parameters']
): Array<{ id: string; label: string }> => {
  if (!parameters?.values) {
    return []
  }

  return [...parameters.values]
    .sort((a, b) => a.index - b.index)
    .map((value, position) => ({
      id: `option_${position}_${value.index}`,
      label: value.name,
    }))
}

/**
 * Custom hook for managing custom forms.
 * Provides methods to fetch, create, and retrieve single forms.
 */
export const useCustomForm = () => {
  const [data, setData] = useState<CustomForm[] | null>(null)
  const [singleForm, setSingleForm] = useState<CustomForm | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // Fetch all forms
  const fetchForms = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const response = await req('/forms')
      setData(response.data as CustomForm[])
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching forms')
      )
    } finally {
      setLoading(false)
    }
  }, [])

  // Create a new form
  const createForm = useCallback(
    async ({ name, id }: CreateFormData): Promise<CustomForm> => {
      setLoading(true)
      setError(null)
      try {
        const response = await req('/forms', {
          method: 'POST',
          body: JSON.stringify({ name, id }),
        })
        await fetchForms()
        return response.data as CustomForm
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while creating form')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchForms]
  )

  // Fetch a single form by ID
  const fetchSingleForm = useCallback(
    async (id: string): Promise<CustomForm> => {
      setLoading(true)
      setError(null)
      try {
        const response = await req(`/forms/${id}`)
        setSingleForm(response.data as CustomForm)
        return response.data as CustomForm
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while fetching form')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // Create a new group for a form
  const createGroup = useCallback(
    async ({
      form_id,
      name,
      description,
      is_enabled,
      is_reserved,
      is_content,
      content,
      index,
    }: CreateGroupData): Promise<unknown> => {
      setLoading(true)
      setError(null)
      try {
        const requestBody: any = {
          form_id,
          name,
          description,
          is_enabled: is_enabled ? 1 : 0,
          is_reserved: is_reserved ? 1 : 0,
          index,
        }

        // Add content-specific properties if this is a content section
        if (is_content) {
          requestBody.is_content = 1
          requestBody.content = content || ''
        }

        const response = await req(`/forms/${form_id}/groups`, {
          method: 'POST',
          body: JSON.stringify(requestBody),
        })
        await fetchSingleForm(form_id)
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while creating group')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Update form name and description
  const updateForm = useCallback(
    async (
      formId: string,
      { name, description }: UpdateFormData
    ): Promise<CustomForm> => {
      setLoading(true)
      setError(null)
      try {
        const response = await req(`/forms/${formId}`, {
          method: 'PUT',
          body: JSON.stringify({ name, description }),
        })
        await fetchSingleForm(formId)
        return response.data as CustomForm
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while updating form')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Delete a group from a form
  const deleteGroup = useCallback(
    async (formId: string, groupId: string): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        await req(`/forms/groups/${groupId}`, {
          method: 'DELETE',
        })
        await fetchSingleForm(formId)
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while deleting group')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Update a group in a form
  const updateGroup = useCallback(
    async (
      formId: string,
      groupId: string,
      updateData: UpdateGroupData
    ): Promise<unknown> => {
      setLoading(true)
      setError(null)
      try {
        // Convert boolean values to numbers for API
        const apiUpdateData: any = { ...updateData }
        if (typeof updateData.is_enabled === 'boolean') {
          apiUpdateData.is_enabled = updateData.is_enabled ? 1 : 0
        }
        if (typeof updateData.is_reserved === 'boolean') {
          apiUpdateData.is_reserved = updateData.is_reserved ? 1 : 0
        }

        const response = await req(`/forms/groups/${groupId}`, {
          method: 'PUT',
          body: JSON.stringify(apiUpdateData),
        })
        await fetchSingleForm(formId)
        return response.data
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while updating group')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Reorder groups in a form
  const reorderGroups = useCallback(
    async (formId: string, groups: ReorderGroupData[]): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        await req(`/forms/${formId}/groups/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ groups }),
        })
        await fetchSingleForm(formId)
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while reordering groups')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Create a new item for a group
  const createItem = useCallback(
    async ({
      group_id,
      name,
      description,
      form_item_type_id,
      is_enabled,
      is_required,
      is_reserved,
      index,
      parameters,
      form_id,
    }: CreateItemData): Promise<FormItem> => {
      setLoading(true)
      setError(null)
      try {
        const response = await req(`/forms/groups/${group_id}/items`, {
          method: 'POST',
          body: JSON.stringify({
            name,
            description,
            form_item_type_id,
            is_enabled: is_enabled ? 1 : 0,
            is_required: is_required ? 1 : 0,
            is_reserved: is_reserved ? 1 : 0,
            index,
            parameters,
          }),
        })
        // Refresh the form to get updated data
        await fetchSingleForm(form_id as string)
        return response.data as FormItem
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while creating item')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Update an item
  const updateItem = useCallback(
    async (
      itemId: string,
      updateData: UpdateItemData,
      formId?: string
    ): Promise<FormItem> => {
      setLoading(true)
      setError(null)
      try {
        // Convert boolean values to numbers for API
        const apiUpdateData: any = { ...updateData }
        if (typeof updateData.is_enabled === 'boolean') {
          apiUpdateData.is_enabled = updateData.is_enabled ? 1 : 0
        }
        if (typeof updateData.is_required === 'boolean') {
          apiUpdateData.is_required = updateData.is_required ? 1 : 0
        }
        if (typeof updateData.is_reserved === 'boolean') {
          apiUpdateData.is_reserved = updateData.is_reserved ? 1 : 0
        }
        // Include parameters if provided
        if (updateData.parameters) {
          apiUpdateData.parameters = updateData.parameters
        }

        const response = await req(`/forms/items/${itemId}`, {
          method: 'PUT',
          body: JSON.stringify(apiUpdateData),
        })
        await fetchSingleForm(formId as string)
        return response.data as FormItem
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while updating item')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Delete an item
  const deleteItem = useCallback(
    async (itemId: string, formId?: string): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        await req(`/forms/items/${itemId}`, {
          method: 'DELETE',
        })
        await fetchSingleForm(formId as string)
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while deleting item')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  // Reorder items in a group
  const reorderItems = useCallback(
    async (
      groupId: string,
      items: ReorderItemData[],
      formId: string
    ): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        await req(`/forms/groups/${groupId}/items/reorder`, {
          method: 'PUT',
          body: JSON.stringify({ items }),
        })
        // Refresh the form to get updated data
        await fetchSingleForm(formId)
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while reordering items')
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchSingleForm]
  )

  return {
    data,
    singleForm,
    loading,
    error,
    fetchForms,
    createForm,
    createGroup,
    updateForm,
    deleteGroup,
    updateGroup,
    reorderGroups,
    fetchSingleForm,
    refetch: fetchForms,
    // New item methods
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  }
}
